import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { justForSideEffect, justForSideEffectWithPayload } from 'src/infrastructure/saga.helpers'
import { Movement } from '../types'
import {
  BatchAndStockBatchDue, BatchInfo, BatchItem, GetAddressSagaParams, GetBatchInfoSagaParams,
  GetBatchParams, MoveBatchSagaParams, UpdateMovementsField, MovementsFilters, PostMovimentationSagaParams,
  StockMovementsState, ValidatedAddress, ValidateSupervisorBadgeParams,
  PkItem, GetPkItemsSagaParams, ValidatePkModelBarcodeSagaParams, PkItemMovementSagaParams, PkItemDidNotFitsSagaParams,
} from './StockMovementsTypes'

const initialState: StockMovementsState = {
  filters: {
    page: 1,
  },
  movements: [],
  totalItems: 0,
  loading: false,
  batchesList: [],
  validatedAddress: false,
  items: [],
  pkModalDisableButtons: false,
}

const getMovements = justForSideEffect()
const validateSupervisorBadge = justForSideEffectWithPayload<ValidateSupervisorBadgeParams>()
const getBatch = justForSideEffectWithPayload<GetBatchParams>()
const moveBatch = justForSideEffectWithPayload<MoveBatchSagaParams>()
const getAddress = justForSideEffectWithPayload<GetAddressSagaParams>()
const getBatchInfo = justForSideEffectWithPayload<GetBatchInfoSagaParams>()
const postMovimentation = justForSideEffectWithPayload<PostMovimentationSagaParams>()
const checkStockAtAddress = justForSideEffectWithPayload<GetAddressSagaParams>()

const addMovements = (state: StockMovementsState, action: PayloadAction<Movement[]>) => ({
  ...state,
  movements: [...state.movements, ...action.payload],
})

const resetMovements = (state: StockMovementsState) => ({
  ...state,
  movements: [],
  filters: {
    ...state.filters,
    page: 1,
  },
})

const setTotalItems = (state: StockMovementsState, action: PayloadAction<number>) => ({
  ...state,
  totalItems: action.payload,
})

const nextPage = (state: StockMovementsState): StockMovementsState => ({
  ...state,
  filters: {
    ...state.filters,
    page: state.filters.page + 1,
  },
})

const setFilters = (state: StockMovementsState, action: PayloadAction<MovementsFilters>) => ({
  ...state,
  filters: action.payload,
})

const setLoad = (
  state: StockMovementsState,
  { payload }: PayloadAction<boolean>,
): StockMovementsState => ({
  ...state,
  loading: payload,
})

const setBatchAndStockBatchDueSelected = (
  state: StockMovementsState,
  { payload }: PayloadAction<BatchAndStockBatchDue | undefined>,
): StockMovementsState => ({
  ...state,
  selectedBatchAndStockBatchDue: payload,
})

const setBadge = (
  state: StockMovementsState,
  { payload }: PayloadAction<string>,
): StockMovementsState => ({
  ...state,
  badge: payload,
})

const setBatchAndStockBatchDueList = (
  state: StockMovementsState,
  { payload }: PayloadAction<BatchAndStockBatchDue[] | undefined>,
): StockMovementsState => ({
  ...state,
  batchAndStockBatchDueList: payload,
})

const setInputAddress = (
  state: StockMovementsState,
  { payload }: PayloadAction<ValidatedAddress>,
): StockMovementsState => ({
  ...state,
  inputAddress: payload,
})

const setBatchInfo = (
  state: StockMovementsState,
  { payload }: PayloadAction<BatchInfo | undefined>,
): StockMovementsState => ({
  ...state,
  batchInfo: payload,
})

const setBatchesList = (
  state: StockMovementsState,
  { payload }: PayloadAction<BatchItem[]>,
): StockMovementsState => ({
  ...state,
  batchesList: payload,
})

const clearState = (): StockMovementsState => initialState

const reset = () => initialState

const updateMovementsField = (state, action: PayloadAction<UpdateMovementsField>) => {
  const { key, value } = action.payload
  return { ...state, [key]: value }
}

const getPkItems = justForSideEffectWithPayload<GetPkItemsSagaParams>()
const validatePkModelBarcode = justForSideEffectWithPayload<ValidatePkModelBarcodeSagaParams>()
const pkItemMovement = justForSideEffectWithPayload<PkItemMovementSagaParams>()
const pkItemDidNotFits = justForSideEffectWithPayload<PkItemDidNotFitsSagaParams>()

const setPkItems = (
  state: StockMovementsState,
  { payload }: PayloadAction<PkItem[]>,
): StockMovementsState => ({
  ...state,
  items: payload,
})

const slice = createSlice({
  name: 'stockMovements',
  initialState,
  reducers: {
    getMovements,
    addMovements,
    resetMovements,
    setTotalItems,
    nextPage,
    setFilters,
    reset,
    setLoad,
    setBadge,
    setBatchAndStockBatchDueList,
    validateSupervisorBadge,
    setBatchAndStockBatchDueSelected,
    clearState,
    getBatch,
    moveBatch,
    getAddress,
    setInputAddress,
    getBatchInfo,
    setBatchInfo,
    setBatchesList,
    postMovimentation,
    updateMovementsField,
    getPkItems,
    validatePkModelBarcode,
    pkItemMovement,
    setPkItems,
    checkStockAtAddress,
    pkItemDidNotFits,
  },
})

export default slice.reducer

export const { actions } = slice
